import { useConfig } from 'domains/config/hooks'
import { useFormControl } from 'domains/forms/hooks'
import { useI18n } from 'domains/i18n/hooks'
import { useVisibility } from 'domains/visibility/hooks'
import { className } from 'lib/css'
import { useCallback, useLayoutEffect, useRef } from 'preact/hooks'
import Form from 'ui/components/form-controls/form'
import Input from 'ui/components/form-controls/input'
import Textarea from 'ui/components/form-controls/textarea'
import Icon from 'ui/components/layout/icon'
import { useSeamlyEntry, useSeamlyTyping } from 'ui/hooks/seamly-entry-hooks'
import { useLiveRegion, useSeamlyCommands } from 'ui/hooks/seamly-hooks'
import { useCharacterLimit, useEntryTextTranslation } from './hooks'

export default function TextEntryForm({
  controlName,
  skipLinkId,
}: {
  controlName: 'textMessageEntry'
  skipLinkId: string
}) {
  const { t } = useI18n()
  const { setInputFocus } = useVisibility()
  const { sendAssertive } = useLiveRegion()
  const { emitEvent } = useSeamlyCommands()
  const handleKeyUp = useSeamlyTyping()
  const { setBlockAutoEntrySwitch } = useSeamlyEntry()
  const { placeholder, label, labelClass } =
    useEntryTextTranslation(controlName)
  // TODO: Standardize the validation on form fields
  const {
    hasCharacterLimit,
    reachedCharacterWarning,
    reachedCharacterLimit,
    remainingChars,
  } = useCharacterLimit(controlName)
  const [{ value }] = useFormControl(controlName)

  const hasValue = !!value

  const { useMultilineUserInput } = useConfig()
  const submitButtonRef = useRef<HTMLButtonElement>(null)

  const handleFocus = useCallback(() => {
    if (reachedCharacterWarning) {
      sendAssertive(t('input.srCharacterLimitText', { limit: remainingChars }))
    }

    emitEvent('ui.inputFocus')
  }, [t, sendAssertive, reachedCharacterWarning, remainingChars, emitEvent])

  // When the input holds a value, the component should be blocked from switching
  // to file upload form.
  useLayoutEffect(() => {
    setBlockAutoEntrySwitch(hasValue)

    return () => {
      setBlockAutoEntrySwitch(false)
    }
  }, [setBlockAutoEntrySwitch, hasValue])

  const handlePointerDown = (event) => {
    // When a message is submitted, the keyboard should be prevented from closing on mobile devices
    event.preventDefault()
  }

  const entryProps = {
    id: skipLinkId,
    name: controlName,
    className: className('input__text'),
    autocomplete: 'off',
    placeholder,
    labelText: label,
    labelClass: className(labelClass),
    'aria-invalid': hasCharacterLimit ? reachedCharacterLimit : false,
    onKeyUp: handleKeyUp,
    onFocus: handleFocus,
    focus: setInputFocus,
  }

  return (
    <Form
      className={className('entry-form')}
      disableValidationClasses
      noValidate="true"
    >
      <div
        className={className([
          'input--text__container',
          ...(reachedCharacterWarning && !reachedCharacterLimit
            ? ['character-warning']
            : []),
          ...(reachedCharacterLimit ? ['character-exceeded'] : []),
        ])}
      >
        {!useMultilineUserInput ? (
          <Input {...entryProps} type="text" />
        ) : (
          <Textarea
            {...entryProps}
            submitButtonRef={submitButtonRef}
            wrap="hard"
          />
        )}

        <div className={className('character-count')}>
          {reachedCharacterWarning && <span>{remainingChars}</span>}
        </div>
      </div>
      <button
        className={className('button', 'input__submit')}
        onPointerDown={handlePointerDown}
        aria-disabled={!hasValue || reachedCharacterLimit}
        ref={submitButtonRef}
        type="submit"
      >
        <Icon name="send" size="32" alt={t('input.sendMessage')} />
      </button>
    </Form>
  )
}
